const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';

export interface User {
  id: string;
  email: string;
  username?: string;
  isAdmin: boolean;
  credits?: number;
  subscriptionStatus?: string;
  emailVerified?: boolean;
  hasPassword?: boolean;
  address?: string;
  city?: string;
  postalCode?: string;
  country?: string;
  phone?: string;
}

export interface AuthResponse {
  success: boolean;
  message: string;
  token?: string;
  user?: User;
}

export interface GoogleAuthResponse {
  success: boolean;
  authUrl?: string;
  message?: string;
  token?: string;
  user?: User;
}

class AuthService {
  private token: string | null = null;

  constructor() {
    // Load token from localStorage on initialization
    this.token = localStorage.getItem('authToken');
    
    // Validate token on initialization if it exists
    if (this.token) {
      this.validateTokenOnInit();
    }
  }

  private async validateTokenOnInit() {
    try {
      // Try to validate the token by making a request to /auth/me
      const response = await fetch(`${API_BASE_URL}/auth/me`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${this.token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        console.log('🔄 Token validation failed on init, clearing token');
        this.clearToken();
      }
    } catch (error) {
      console.log('🔄 Token validation error on init, clearing token');
      this.clearToken();
    }
  }

  private setToken(token: string) {
    this.token = token;
    localStorage.setItem('authToken', token);
  }

  private clearToken() {
    this.token = null;
    localStorage.removeItem('authToken');
  }

  private async makeRequest(endpoint: string, options: RequestInit = {}): Promise<any> {
    const url = `${API_BASE_URL}${endpoint}`;
    
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    if (this.token) {
      headers.Authorization = `Bearer ${this.token}`;
    }

    // Merge with any additional headers from options
    if (options.headers) {
      Object.assign(headers, options.headers);
    }

    try {
      const response = await fetch(url, {
        ...options,
        headers,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        
        // Handle 401 errors by clearing the token
        if (response.status === 401) {
          console.log('🔄 Token expired or invalid, clearing from storage');
          this.clearToken();
          
          // If this is not a login request, redirect to login
          if (!endpoint.includes('/auth/login') && !endpoint.includes('/auth/signup')) {
            // Use a more graceful approach instead of immediate redirect
            // This allows the calling code to handle the error appropriately
            throw new Error('UNAUTHORIZED');
          }
        }
        
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      return response.json();
    } catch (error) {
      // If it's a network error (server not running), provide a fallback
      if (error instanceof TypeError && error.message.includes('fetch')) {
        console.warn('Server not available, using fallback authentication');
        throw new Error('Server not available. Please start the server to use real authentication.');
      }
      
      // Re-throw the error to be handled by the calling code
      throw error;
    }
  }

  async login(email: string, password: string, recaptchaToken?: string): Promise<AuthResponse> {
    try {
      // Clear any existing token before attempting login
      // This ensures we start fresh and don't have conflicts with expired tokens
      if (this.token) {
        console.log('🔄 Clearing existing token before login attempt');
        this.clearToken();
      }

      const requestBody: any = { email, password };
      if (recaptchaToken) {
        requestBody.recaptchaToken = recaptchaToken;
      }

      const response = await this.makeRequest('/auth/login', {
        method: 'POST',
        body: JSON.stringify(requestBody),
      });

      if (response.token) {
        this.setToken(response.token);
      }

      return {
        success: true,
        message: response.message,
        token: response.token,
        user: response.user,
      };
    } catch (error: any) {
      // Handle specific email verification error
      if (error.message && error.message.includes('Email not verified')) {
        throw new Error('EMAIL_NOT_VERIFIED');
      }
      throw error;
    }
  }

  async signup(email: string, password: string, username?: string, recaptchaToken?: string): Promise<AuthResponse> {
    try {
      const requestBody: any = { email, password };
      if (username) {
        requestBody.username = username;
      }
      if (recaptchaToken) {
        requestBody.recaptchaToken = recaptchaToken;
      }

      const response = await this.makeRequest('/auth/signup', {
        method: 'POST',
        body: JSON.stringify(requestBody),
      });

      if (response.token) {
        this.setToken(response.token);
      }

      return {
        success: true,
        message: response.message,
        token: response.token,
        user: response.user,
      };
    } catch (error) {
      throw error;
    }
  }

  async getGoogleAuthUrl(): Promise<GoogleAuthResponse> {
    try {
      const response = await this.makeRequest('/auth/google/url', {
        method: 'GET',
      });

      return {
        success: true,
        authUrl: response.authUrl,
      };
    } catch (error) {
      throw error;
    }
  }

  async handleGoogleCallback(code: string): Promise<AuthResponse> {
    try {
      const response = await this.makeRequest('/auth/google/callback', {
        method: 'POST',
        body: JSON.stringify({ code }),
      });

      if (response.token) {
        this.setToken(response.token);
      }

      return {
        success: true,
        message: response.message,
        token: response.token,
        user: response.user,
      };
    } catch (error) {
      throw error;
    }
  }

  async getCurrentUser(): Promise<User | null> {
    try {
      if (!this.token) {
        return null;
      }

      const response = await this.makeRequest('/auth/me', {
        method: 'GET',
      });

      return response.user;
    } catch (error: any) {
      // If token is invalid or server is not available, clear it
      if (error.message === 'UNAUTHORIZED' || error.message.includes('401')) {
        console.log('🔄 Clearing invalid token during getCurrentUser');
        this.clearToken();
      }
      return null;
    }
  }

  async logout(): Promise<void> {
    try {
      if (this.token) {
        await this.makeRequest('/auth/logout', {
          method: 'POST',
        });
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      this.clearToken();
    }
  }

  // Force logout without authentication (useful for clearing invalid tokens)
  async forceLogout(): Promise<void> {
    try {
      // Make request without token
      const response = await fetch(`${API_BASE_URL}/auth/force-logout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        console.warn('Force logout request failed, but continuing with token cleanup');
      }
    } catch (error) {
      console.warn('Force logout request failed, but continuing with token cleanup:', error);
    } finally {
      this.clearToken();
    }
  }

  async requestPasswordReset(email: string, recaptchaToken?: string): Promise<{ success: boolean; message: string }> {
    try {
      const requestBody: any = { email };
      if (recaptchaToken) {
        requestBody.recaptchaToken = recaptchaToken;
      }

      const response = await this.makeRequest('/auth/password-reset', {
        method: 'POST',
        body: JSON.stringify(requestBody),
      });

      return {
        success: true,
        message: response.message,
      };
    } catch (error) {
      throw error;
    }
  }

  async resetPassword(token: string, newPassword: string): Promise<{ success: boolean; message: string }> {
    try {
      const response = await this.makeRequest(`/auth/password-reset/${token}`, {
        method: 'POST',
        body: JSON.stringify({ password: newPassword }),
      });

      return {
        success: true,
        message: response.message,
      };
    } catch (error) {
      throw error;
    }
  }

  async verifyEmail(token: string): Promise<{ success: boolean; message: string }> {
    try {
      const response = await this.makeRequest(`/auth/verify-email?token=${token}`, {
        method: 'GET',
      });

      return {
        success: true,
        message: response.message,
      };
    } catch (error) {
      throw error;
    }
  }

  async resendVerificationEmail(email: string): Promise<{ success: boolean; message: string }> {
    try {
      const response = await this.makeRequest('/auth/resend-verification', {
        method: 'POST',
        body: JSON.stringify({ email }),
      });

      return {
        success: true,
        message: response.message,
      };
    } catch (error) {
      throw error;
    }
  }

  async changePassword(currentPassword: string, newPassword: string): Promise<{ success: boolean; message: string }> {
    try {
      const response = await this.makeRequest('/user/change-password', {
        method: 'PUT',
        body: JSON.stringify({ currentPassword, newPassword }),
      });

      return {
        success: true,
        message: response.message,
      };
    } catch (error) {
      throw error;
    }
  }

  async deleteAccount(): Promise<{ success: boolean; message: string }> {
    try {
      const response = await this.makeRequest('/user/account', {
        method: 'DELETE',
      });

      return {
        success: true,
        message: response.message,
      };
    } catch (error) {
      throw error;
    }
  }

  isAuthenticated(): boolean {
    return !!this.token;
  }

  getToken(): string | null {
    return this.token;
  }

  // Force clear token (useful for debugging or manual logout)
  forceClearToken(): void {
    console.log('🔄 Force clearing token');
    this.clearToken();
  }

  // Check if token is valid by making a test request
  async isTokenValid(): Promise<boolean> {
    if (!this.token) {
      return false;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/auth/me`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${this.token}`,
          'Content-Type': 'application/json',
        },
      });

      return response.ok;
    } catch (error) {
      return false;
    }
  }
}

export const authService = new AuthService(); 